import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';

// Types
export interface KeyResult {
  id: string;
  okr_id: string;
  description: string;
  target_value: number;
  current_value: number;
  unit: string;
  status: 'on_track' | 'at_risk' | 'behind' | 'completed';
  progress_percentage: number;
  commitment_type?: 'committed' | 'aspirational';
  owner_id?: string;
  owner?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  reference_number?: number; // Sequential number within OKR (1, 2, 3)
  created_at: string;
  updated_at: string;
}

export interface OrganizationOKR {
  id: string;
  organization_id: string;
  planning_cycle_id?: string;
  position_id?: string;
  area_id?: string;
  objective: string;
  description?: string;
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  progress_percentage?: number; // Made optional since it's calculated
  commitment_type?: 'committed' | 'aspirational';
  score?: number;
  auto_score?: boolean;
  score_justification?: string;
  owner_id?: string;
  owner?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  parent_okr_id?: string;
  parent_okr?: {
    id: string;
    objective: string;
  };
  is_corporate: boolean;
  order_index: number;
  reference_number?: number; // Human-friendly reference like OKR-42
  formatted_reference?: string; // Formatted as "OKR-42"
  created_at: string;
  updated_at: string;
  key_results: KeyResult[];
  child_okrs?: OrganizationOKR[];
  relationships?: Array<{
    id: string;
    related_okr_id: string;
    relationship_type:
      | 'supports'
      | 'depends_on'
      | 'conflicts_with'
      | 'contributes_to'
      | 'aligned_with';
    contribution_notes?: string;
    related_okr?: {
      id: string;
      objective: string;
    };
  }>;
}

export interface OKRFilters {
  planning_cycle_id?: string;
  owner_id?: string;
  is_corporate?: boolean;
  status?: string;
  commitment_type?: string;
  search?: string;
  include_children?: boolean;
  include_relationships?: boolean;
  include_orphaned?: boolean;
}

export interface OKRCreate {
  objective: string;
  description?: string;
  planning_cycle_id?: string;
  position_id?: string;
  area_id?: string;
  owner_id?: string;
  parent_okr_id?: string;
  is_corporate?: boolean;
  status?: 'draft' | 'active' | 'completed' | 'cancelled';
  commitment_type?: 'committed' | 'aspirational';
  order_index?: number;
  key_results?: Array<{
    description: string;
    target_value: number;
    unit: string;
    commitment_type?: 'committed' | 'aspirational';
  }>;
  relationships?: Array<{
    relationship_type: string;
    related_object_id: string;
    contribution_notes?: string;
  }>;
}

export interface OKRUpdate {
  objective?: string;
  description?: string;
  status?: 'draft' | 'active' | 'completed' | 'cancelled';
  owner_id?: string;
  area_id?: string;
  commitment_type?: 'committed' | 'aspirational';
  is_corporate?: boolean;
  auto_score?: boolean;
  score?: number;
  score_justification?: string;
  planning_cycle_id?: string;
}

export interface KeyResultCreate {
  description: string;
  target_value: number;
  unit: string;
  commitment_type?: 'committed' | 'aspirational';
  owner_id?: string;
}

export interface KeyResultUpdate {
  description?: string;
  target_value?: number;
  current_value?: number;
  unit?: string;
  status?: 'on_track' | 'at_risk' | 'behind' | 'completed';
  owner_id?: string;
  commitment_type?: 'committed' | 'aspirational';
}

// OKR Service
export const okrService = {
  // List all OKRs for an organization
  async listOKRs(
    organizationId: string,
    filters?: OKRFilters,
  ): Promise<OrganizationOKR[]> {
    const params = new URLSearchParams({ organization_id: organizationId });

    if (filters) {
      if (filters.planning_cycle_id)
        params.append('planning_cycle_id', filters.planning_cycle_id);
      if (filters.owner_id) params.append('owner_id', filters.owner_id);
      if (filters.is_corporate !== undefined)
        params.append('is_corporate', String(filters.is_corporate));
      if (filters.status) params.append('status', filters.status);
      if (filters.commitment_type)
        params.append('commitment_type', filters.commitment_type);
      if (filters.search) params.append('search', filters.search);
      if (filters.include_children) params.append('include_children', 'true');
      if (filters.include_relationships)
        params.append('include_relationships', 'true');
      if (filters.include_orphaned) params.append('include_orphaned', 'true');
    }

    const response = await axios.get(`${API_URL}/okrs?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  // Get a single OKR
  async getOKR(
    okrId: string,
    organizationId: string,
  ): Promise<OrganizationOKR> {
    const response = await axios.get(
      `${API_URL}/okrs/${okrId}?organization_id=${organizationId}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  // Create a new OKR
  async createOKR(
    organizationId: string,
    data: OKRCreate,
  ): Promise<OrganizationOKR> {
    console.log('Creating OKR with data:', data);
    const response = await axios.post(
      `${API_URL}/okrs?organization_id=${organizationId}`,
      data,
      {
        headers: getAuthHeaders(),
      },
    );
    console.log('OKR created, response:', response.data);
    return response.data;
  },

  // Update an OKR
  async updateOKR(
    okrId: string,
    organizationId: string,
    data: OKRUpdate,
  ): Promise<OrganizationOKR> {
    const response = await axios.put(
      `${API_URL}/okrs/${okrId}?organization_id=${organizationId}`,
      data,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  // Delete an OKR
  async deleteOKR(
    okrId: string,
    organizationId: string,
    force = false,
  ): Promise<{ message: string; cleanup_summary?: any }> {
    const response = await axios.delete(
      `${API_URL}/okrs/${okrId}?organization_id=${organizationId}&force=${force}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  // Add a key result to an OKR
  async addKeyResult(
    okrId: string,
    organizationId: string,
    data: KeyResultCreate,
  ): Promise<KeyResult> {
    const response = await axios.post(
      `${API_URL}/okrs/${okrId}/key-results?organization_id=${organizationId}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  // Update a key result
  async updateKeyResult(
    okrId: string,
    keyResultId: string,
    organizationId: string,
    data: KeyResultUpdate,
  ): Promise<KeyResult> {
    const response = await axios.put(
      `${API_URL}/okrs/${okrId}/key-results/${keyResultId}?organization_id=${organizationId}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  // Delete a key result
  async deleteKeyResult(
    okrId: string,
    keyResultId: string,
    organizationId: string,
  ): Promise<void> {
    await axios.delete(
      `${API_URL}/okrs/${okrId}/key-results/${keyResultId}?organization_id=${organizationId}`,
      {
        headers: getAuthHeaders(),
      },
    );
  },

  // Get OKR hierarchy (corporate objectives with cascading)
  async getOKRHierarchy(
    organizationId: string,
    planningCycleId?: string,
  ): Promise<OrganizationOKR[]> {
    const params = new URLSearchParams({
      organization_id: organizationId,
      include_children: 'true',
      include_relationships: 'true',
    });

    if (planningCycleId) {
      params.append('planning_cycle_id', planningCycleId);
    }

    const response = await axios.get(
      `${API_URL}/okrs/hierarchy?${params.toString()}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  // Bulk update progress for multiple key results
  async bulkUpdateProgress(
    organizationId: string,
    updates: Array<{ key_result_id: string; current_value: number }>,
  ): Promise<void> {
    await axios.post(
      `${API_URL}/okrs/bulk-progress?organization_id=${organizationId}`,
      { updates },
      { headers: getAuthHeaders() },
    );
  },

  // Get OKR statistics and analytics
  async getOKRStatistics(
    organizationId: string,
    planningCycleId?: string,
  ): Promise<any> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (planningCycleId) params.append('planning_cycle_id', planningCycleId);

    const response = await axios.get(
      `${API_URL}/okrs/statistics?${params.toString()}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  // Update OKR order
  async updateOKROrder(
    organizationId: string,
    orderUpdate: { items: Array<{ id: string; order_index: number }> },
  ): Promise<OrganizationOKR[]> {
    const response = await axios.put(
      `${API_URL}/okrs/bulk/update-order?organization_id=${organizationId}`,
      orderUpdate,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },
};
